/**
 * AdminManager — Extracted from App class.
 * Handles admin-only user management, allowed users, waitlist, and privileged operations.
 */
import { AppEventType } from './appEvents';
export class AdminManager {
    constructor(deps) {
        this.deps = deps;
    }
    get api() { return this.deps.getApi(); }
    get cache() { return this.deps.dataCache; }
    get events() { return this.deps.eventEmitter; }
    get logger() { return this.deps.logger; }
    async getAllUsers(windowMs) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.getAllUsers(windowMs));
        }
        catch (error) {
            this.deps.handleError('getAllUsers', this.deps.toError(error));
            throw error;
        }
    }
    async getAllOrgs(windowMs) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.getAllOrgs(windowMs));
            return result.organizations;
        }
        catch (error) {
            this.deps.handleError('getAllOrgs', this.deps.toError(error));
            throw error;
        }
    }
    async updateOrgSubscription(orgId, patch) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.adminUpdateSubscription(orgId, patch));
            return result.organization;
        }
        catch (error) {
            this.deps.handleError('updateOrgSubscription', this.deps.toError(error));
            throw error;
        }
    }
    async moveUser(userId, body) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.adminMoveUser(userId, body));
        }
        catch (error) {
            this.deps.handleError('moveUser', this.deps.toError(error));
            throw error;
        }
    }
    async removeVerifiedPhone(orgId, userId) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.adminRemoveVerifiedPhone(orgId, userId));
        }
        catch (error) {
            this.deps.handleError('removeVerifiedPhone', this.deps.toError(error));
            throw error;
        }
    }
    async unlinkUserPhoneProvider(userId) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.adminUnlinkUserPhoneProvider(userId));
        }
        catch (error) {
            this.deps.handleError('unlinkUserPhoneProvider', this.deps.toError(error));
            throw error;
        }
    }
    async getPhoneVerificationStats(windowMs) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.adminGetPhoneVerificationStats(windowMs));
        }
        catch (error) {
            this.deps.handleError('getPhoneVerificationStats', this.deps.toError(error));
            throw error;
        }
    }
    async getTwilioStatus(limit) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.adminGetTwilioStatus(limit));
        }
        catch (error) {
            this.deps.handleError('getTwilioStatus', this.deps.toError(error));
            throw error;
        }
    }
    /** Every workspace's communication counters for the last `days` days (at most 90), oldest first. */
    async getCommPlatformMetrics(days) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.getAdminCommMetrics(days));
        }
        catch (error) {
            this.deps.handleError('getCommPlatformMetrics', this.deps.toError(error));
            throw error;
        }
    }
    /** A page of recorded communication outcomes, newest first. */
    async getCommEvents(filter) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.getAdminCommEvents(filter));
        }
        catch (error) {
            this.deps.handleError('getCommEvents', this.deps.toError(error));
            throw error;
        }
    }
    /** List every WhatsApp template in the Twilio account (with live approval status). */
    async listWhatsAppTemplates(forceRefresh = false) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.adminListWhatsAppTemplates(forceRefresh));
        }
        catch (error) {
            this.deps.handleError('listWhatsAppTemplates', this.deps.toError(error));
            throw error;
        }
    }
    /** Fire a test send of a template to any number for verification (no retry). */
    async sendWhatsAppTemplateTest(contentSid, payload) {
        this.deps.ensureAuthenticated();
        try {
            // No retry — a duplicate send would fire two messages, which is worse
            // than a false error at the UI.
            return await this.api.adminSendWhatsAppTemplate(contentSid, payload);
        }
        catch (error) {
            this.deps.handleError('sendWhatsAppTemplateTest', this.deps.toError(error));
            throw error;
        }
    }
    async getUserSnapshot(userId) {
        this.deps.ensureAuthenticated();
        try {
            return await this.deps.makeApiCallWithRetry(() => this.api.adminGetUserSnapshot(userId));
        }
        catch (error) {
            this.deps.handleError('getUserSnapshot', this.deps.toError(error));
            throw error;
        }
    }
    async createAllowedUser(name, email, role) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.createAllowedUser({ name, email, role }));
            return result.allowedUser;
        }
        catch (error) {
            this.deps.handleError('createAllowedUser', this.deps.toError(error));
            throw error;
        }
    }
    async updateUser(userId, userData) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.updateUser(userId, userData));
            if (result.success && result.userData) {
                const event = { userData: result.userData, timestamp: new Date() };
                this.events.emit(AppEventType.USERDATA_UPDATED, event);
                this.logger.info('🔄 User updated, emitted USERDATA_UPDATED event for user:', result.userData.id);
            }
            return result.userData;
        }
        catch (error) {
            this.deps.handleError('updateUser', this.deps.toError(error));
            throw error;
        }
    }
    async deleteUser(userId) {
        this.deps.ensureAuthenticated();
        try {
            await this.deps.makeApiCallWithRetry(() => this.api.deleteUser(userId));
        }
        catch (error) {
            this.deps.handleError('deleteUser', this.deps.toError(error));
            throw error;
        }
    }
    async getAllowedUsers(windowMs) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.getAllowedUsers(windowMs));
            return result.allowedUsers;
        }
        catch (error) {
            this.deps.handleError('getAllowedUsers', this.deps.toError(error));
            throw error;
        }
    }
    async removeAllowedUser(email) {
        this.deps.ensureAuthenticated();
        try {
            await this.deps.makeApiCallWithRetry(() => this.api.removeAllowedUser(email));
        }
        catch (error) {
            this.deps.handleError('removeAllowedUser', this.deps.toError(error));
            throw error;
        }
    }
    async getWaitlist(windowMs) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.getWaitlist(windowMs));
            return result.entries;
        }
        catch (error) {
            this.deps.handleError('getWaitlist', this.deps.toError(error));
            throw error;
        }
    }
    async getAuditLeads(windowMs) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.getAuditLeads(windowMs));
            return result.entries;
        }
        catch (error) {
            this.deps.handleError('getAuditLeads', this.deps.toError(error));
            throw error;
        }
    }
    async getQuizLeads(windowMs) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.getQuizLeads(windowMs));
            return result.entries;
        }
        catch (error) {
            this.deps.handleError('getQuizLeads', this.deps.toError(error));
            throw error;
        }
    }
    async getFeedback(windowMs) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.getFeedback(windowMs));
            return result.entries;
        }
        catch (error) {
            this.deps.handleError('getFeedback', this.deps.toError(error));
            throw error;
        }
    }
    async updateUserPrivileged(userId, updates, reason) {
        this.deps.ensureAuthenticated();
        try {
            const result = await this.deps.makeApiCallWithRetry(() => this.api.updateUserPrivileged(userId, updates, reason));
            if (result.success && result.userData) {
                const event = { userData: result.userData, timestamp: new Date() };
                this.events.emit(AppEventType.USERDATA_UPDATED, event);
                this.logger.info('🔄 User updated with privileged access, emitted USERDATA_UPDATED event for user:', result.userData.id);
            }
            this.logger.info('User updated successfully with privileged access', { userId, updates, reason });
            return result.userData;
        }
        catch (error) {
            this.deps.handleError('updateUserPrivileged', this.deps.toError(error));
            throw error;
        }
    }
    async fetchUserData(userId) {
        this.deps.ensureAuthenticated();
        try {
            this.logger.info('📋 Fetching user data from API...');
            const response = await this.deps.makeApiCallWithRetry(() => this.api.getUserDetails(userId));
            this.cache.updateData({ userData: response.userData, timestamp: Date.now() });
            const event = { userData: response.userData, timestamp: new Date() };
            this.logger.info('📋 User data fetched successfully, emitting USERDATA_UPDATED event', event);
            this.events.emit(AppEventType.USERDATA_UPDATED, event);
            return response.userData;
        }
        catch (error) {
            this.deps.handleError('fetchUserData', this.deps.toError(error));
            throw error;
        }
    }
}
